<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Autoplay, Navigation } from 'swiper/modules';
import TestimonialCard from '~/components/cards/TestimonialCard.vue';
import type { TestimonialItem, TestimonialSliderType } from '~/types';

withDefaults(
  defineProps<{
    items: TestimonialItem[];
    styleType?: keyof typeof TestimonialSliderType;
    isRightToLeft?: boolean;
  }>(),
  {
    styleType: 'jungle',
  },
);

const cardColor: Record<keyof typeof TestimonialSliderType, string> = {
  'light-purple': 'bg-viola-50',
  'jungle': '',
};
</script>

<template>
  <div class="w-full">
    <Swiper
      :modules="[Autoplay, Navigation]"
      :autoplay="{
        delay: 7000,
        disableOnInteraction: true,
        reverseDirection: isRightToLeft,
      }"
      :slides-per-view="1"
      :space-between="18"
      :breakpoints="{
        768: {
          slidesPerView: 2,
        },
        1024: {
          slidesPerView: 3,
        },
      }"
      :navigation="{
        enabled: true,
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      }"
      :initial-slide="isRightToLeft ? items.length - 1 : 0"
      class="!overflow-visible testimonial-slider"
    >
      <SwiperSlide
        v-for="(item, idx) in items"
        :key="idx"
        class="!h-auto"
      >
        <TestimonialCard
          v-bind="item"
          :class="[cardColor[styleType], 'h-full']"
        />
      </SwiperSlide>

      <div class="navigation-wrapper">
        <div class="swiper-button-prev" />
        <div class="swiper-button-next" />
      </div>
    </Swiper>
  </div>
</template>

<style lang="scss">
.testimonial-slider {
  .navigation-wrapper {
    @apply relative inset-auto #{!important};
    @apply mt-10 sm:mt-15;
    @apply flex justify-center items-center gap-4 sm:gap-6;
  }

  .swiper-button-prev,
  .swiper-button-next {
    @apply text-white w-8 h-8 sm:w-10 sm:h-10 4k:w-12 4k:h-12;
    @apply flex items-center justify-center;
    @apply transition-transform hover:scale-110;
    color: white !important;
    --swiper-navigation-size: 0;
    position: static !important;
    margin: 0 !important;
  }

  .swiper-button-prev::after {
    content: "";
    display: inline-block;
    width: 100%;
    height: 100%;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    background-image: url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="white"%3E%3Cpath stroke-linecap="round" stroke-linejoin="round" stroke-width="4" d="M15 19l-7-7 7-7"/%3E%3C/svg%3E');
  }

  .swiper-button-next::after {
    content: "";
    display: inline-block;
    width: 100%;
    height: 100%;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    background-image: url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="white"%3E%3Cpath stroke-linecap="round" stroke-linejoin="round" stroke-width="4" d="M9 5l7 7-7 7"/%3E%3C/svg%3E');
  }

  .swiper-button-disabled {
    @apply opacity-50 cursor-not-allowed;
  }
}
</style>
